import React from "react";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Divider from "@mui/material/Divider";
import { IconDefinition } from "@fortawesome/fontawesome-common-types";
import {
  faBars,
  faHouse as faHouseSolid,
  faSchoolFlag as faSchoolFlagSolid,
  faUserGraduate as faUserGraduateSolid,
  faChartColumn as faChartColumnSolid,
  faFolderBookmark as faFolderBookmarkSolid,
  faGear as faGearSolid,
  faSchool as faSchoolSolid,
  faBuildingColumns as faBuildingColumnsSolid,
  faCalendarCircleExclamation as faCalendarCircleExclamationSolid,
  faChartLine as faChartLineSolid,
} from "@fortawesome/pro-solid-svg-icons";
import {
  faHouse as faHouseRegular,
  faSchoolFlag as faSchoolFlagRegular,
  faUserGraduate as faUserGraduateRegular,
  faChartColumn as faChartColumnRegular,
  faFolderBookmark as faFolderBookmarkRegular,
  faGear as faGearRegular,
  faSchool as faSchoolRegular,
  faBuildingColumns as faBuildingColumnsRegular,
  faCalendarCircleExclamation as faCalendarCircleExclamationRegular,
  faChartLine as faChartLineRegular,
  faChevronDown,
} from "@fortawesome/pro-regular-svg-icons";
import Image from "react-bootstrap/Image";
import { Nav, Stack } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Account, AccountContext } from "./context/AccountContext";
import { TabEnum, TabsContext } from "./context/TabsContext";
import { NavContext } from "./context/NavContext";
import { VerifyAgeContext } from "../common/context/VerifyAgeContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useLiveData } from "./utils";
import {
  isUserSignedUp,
  isSchoolUser,
  isSchoolUserTeacher,
  isNonStudentSchoolUser,
} from "./api";
import {
  AnalyticsContext,
  TabTapEvent,
} from "../common/context/AnalyticsContext";
import { SuperTeacherFooter } from "./Footer";
import {
  canSwitchProfile,
  ProfileSwitcherItems,
  ProfileSwitcherMenu,
} from "./ProfileSwitcherMenu";
import { AppLink, SingleLineText } from "../common/CommonStyles";
import {
  COLOR_TEXT_SECONDARY,
  COLOR_TEXT_PRIMARY,
  COLOR_NAV_LINK_ACTIVE,
  COLOR_TAB_ICON_UNSELECTED,
  COLOR_SURFACE_CONTAINER,
  COLOR_SURFACE_CONTAINER_ALPHA,
  COLOR_NEUTRAL_BORDER,
  COLOR_WHITE,
} from "./colors";
import { SPACE_LG, SPACE_XL, SPACE_3XL } from "./spacing";

const Header = styled.div`
  align-content: center;
  height: 64px;
  min-height: 64px;
  padding: 5px 24px;
  background-color: ${COLOR_SURFACE_CONTAINER};
  border-bottom: 1px solid ${COLOR_NEUTRAL_BORDER};
`;

const Sidebar = styled.div`
  min-width: 190px;
  max-width: 190px;
  padding: 10px 8px 16px;
  background-color: ${COLOR_WHITE};
  border-right: 1px solid ${COLOR_NEUTRAL_BORDER};
  display: flex;
  flex-direction: column;
  .nav-pills {
    flex-wrap: nowrap;
    align-items: stretch;
    gap: 8px;
  }
  .nav-pills .nav-link {
    display: flex;
    align-items: center;
    font-family: "Inter";
    font-weight: 400;
    font-size: 15px;
    padding: 7px 14px;
    line-height: 22px;
    --bs-nav-pills-border-radius: 25px;
    --bs-nav-link-color: ${COLOR_TEXT_SECONDARY};
    --bs-nav-link-hover-color: ${COLOR_TEXT_PRIMARY};
  }
  .nav-pills .nav-link:hover:not(.active) {
    background-color: ${COLOR_SURFACE_CONTAINER};
  }
  .nav-pills .nav-link.active {
    --bs-nav-pills-link-active-bg: ${COLOR_SURFACE_CONTAINER};
    --bs-nav-pills-link-active-color: ${COLOR_NAV_LINK_ACTIVE};
    box-shadow: inset 0 0 0 1px ${COLOR_SURFACE_CONTAINER_ALPHA};
  }
`;

const ContentArea = styled.div`
  padding: clamp(${SPACE_LG}px, 2.5vw, ${SPACE_XL}px)
    clamp(${SPACE_LG}px, 4vw, ${SPACE_3XL}px);
`;

const SidebarSectionHeader = styled.div`
  padding: 18px 14px 8px;
  font-family: "Inter";
  font-size: 13px;
  line-height: 18px;
  color: ${COLOR_TEXT_SECONDARY};
`;

const TabIconSlot = styled.span<{ $isSelected: boolean }>`
  display: inline-flex;
  flex-shrink: 0;
  justify-content: center;
  width: 16px;
  margin-right: 10px;
  color: ${(props) =>
    props.$isSelected ? COLOR_NAV_LINK_ACTIVE : COLOR_TAB_ICON_UNSELECTED};
  svg {
    height: 16px;
  }
`;

const TabIcon: React.FC<{
  solid: IconDefinition;
  regular: IconDefinition;
  isSelected: boolean;
}> = ({ solid, regular, isSelected }) => (
  <TabIconSlot $isSelected={isSelected}>
    <FontAwesomeIcon icon={isSelected ? solid : regular} />
  </TabIconSlot>
);

const ProfileMenuChevron = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  margin-left: auto;
  border-radius: 50%;
  color: ${COLOR_TEXT_SECONDARY};
  svg {
    height: 11px;
  }
  &:hover {
    color: ${COLOR_TEXT_PRIMARY};
    background-color: ${COLOR_SURFACE_CONTAINER_ALPHA};
  }
`;

const TouchProfileMenuChevron = styled(ProfileMenuChevron)`
  width: 36px;
  height: 36px;
  margin: -8px -10px -8px 4px;
  svg {
    transition: transform 150ms;
  }
  &[aria-expanded="true"] svg {
    transform: rotate(180deg);
  }
`;

const ProfileMenuToggle: React.FC<{
  isExpanded: boolean;
  isTouch: boolean;
  onToggle: () => void;
}> = ({ isExpanded, isTouch, onToggle }) => {
  const Chevron = isTouch ? TouchProfileMenuChevron : ProfileMenuChevron;
  return (
    <Chevron
      role="button"
      tabIndex={0}
      aria-haspopup={isTouch ? undefined : "menu"}
      aria-expanded={isExpanded}
      onClick={(event) => {
        event.stopPropagation();
        onToggle();
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          event.stopPropagation();
          onToggle();
        }
      }}
    >
      <FontAwesomeIcon icon={faChevronDown} />
    </Chevron>
  );
};

const PRACTICE_TAB_ICON_SOLID_PATH =
  "M3.91,3C3.91,1.345 5.255,0 6.91,0L21.91,0C23.565,0 24.91,1.345 24.91,3L24.91,12C24.91,12.684 24.676,13.313 24.291,13.819L22.562,7.355C21.812,4.556 18.934,2.892 16.13,3.642L3.91,6.919L3.91,3ZM0.104,13.373C-0.327,11.775 0.624,10.13 2.223,9.698L16.712,5.817C18.31,5.391 19.955,6.338 20.387,7.936L22.716,16.631C23.148,18.23 22.196,19.875 20.598,20.306L6.109,24.188C4.505,24.619 2.86,23.667 2.434,22.069L0.104,13.373Z";

const PRACTICE_TAB_ICON_REGULAR_PATH =
  "M3.929,2.695C4.084,1.181 5.359,0 6.915,0L21.915,0L22.22,0.014C23.631,0.159 24.756,1.28 24.896,2.691L24.911,2.995L24.911,11.995L24.896,12.3C24.84,12.867 24.62,13.383 24.296,13.809L22.665,7.716L22.665,2.991C22.665,2.578 22.328,2.241 21.915,2.241L6.915,2.241C6.503,2.241 6.165,2.578 6.165,2.991L6.165,6.305L3.915,6.905L3.915,2.991L3.929,2.686L3.929,2.695ZM0.039,13.073C-0.205,11.573 0.723,10.102 2.223,9.698L16.712,5.817L17.012,5.752C18.414,5.527 19.787,6.319 20.293,7.645L20.387,7.936L22.717,16.631L22.778,16.931C23.003,18.333 22.211,19.706 20.884,20.212L20.593,20.306L6.104,24.188C4.604,24.591 3.067,23.78 2.528,22.359L2.434,22.069L0.104,13.373L0.039,13.073ZM2.279,12.792L4.609,21.483C4.717,21.881 5.129,22.12 5.528,22.013L20.017,18.131C20.415,18.024 20.654,17.611 20.546,17.212L18.217,8.522C18.109,8.123 17.697,7.884 17.298,7.992L2.809,11.873C2.411,11.981 2.172,12.394 2.279,12.792Z";

const PracticeTabIcon: React.FC<{ isSelected: boolean }> = ({ isSelected }) => (
  <TabIconSlot $isSelected={isSelected}>
    <svg viewBox="-0.5 -0.5 26 26" fill="currentColor">
      <path
        d={
          isSelected
            ? PRACTICE_TAB_ICON_SOLID_PATH
            : PRACTICE_TAB_ICON_REGULAR_PATH
        }
      />
    </svg>
  </TabIconSlot>
);

const PROFILE_TAB_ICON_SOLID_PATH =
  "M12,0C18.6274,0 24,5.37258 24,12C24,17.3167 20.5413,21.8229 15.752,23.3984L15.752,16.3877C17.1154,15.722 18.3017,14.69 19.1592,13.3779L20.0127,12.0703C20.4617,11.3772 20.2645,10.449 19.5723,9.99414C18.8787,9.5396 17.95,9.73667 17.4951,10.4346L16.6426,11.7373C15.621,13.3025 13.8805,14.2495 12.0107,14.25C11.4206,14.25 10.8429,14.1564 10.2949,13.9785C10.2533,13.9646 10.2106,13.945 10.1689,13.9355C9.05826,13.5419 8.08241,12.7965 7.41211,11.7891L6.49805,10.416C6.03893,9.72734 5.11082,9.54453 4.42187,10.0029C3.73286,10.4623 3.54562,11.3902 4.00488,12.084L4.91895,13.4531C5.76733,14.723 6.92567,15.7218 8.25195,16.3779L8.25195,23.3994C3.46061,21.825 0,17.3182 0,12C0,5.37258 5.37258,0 12,0ZM12.001,6C11.2057,6 10.4414,6.31674 9.87891,6.87891C9.31645,7.44149 9.00098,8.20447 9.00098,9C9.00098,9.79553 9.31645,10.5585 9.87891,11.1211C10.4414,11.6833 11.2057,12 12.001,12C12.7959,11.9995 13.5599,11.6833 14.1221,11.1211C14.6841,10.5586 15.001,9.7952 15.001,9C15.001,8.2048 14.6841,7.44142 14.1221,6.87891C13.5599,6.31674 12.7959,6.0005 12.001,6Z";

const PROFILE_TAB_ICON_REGULAR_FIGURE_PATH =
  "M14.9999,9C14.9999,8.20435 14.6839,7.44129 14.1213,6.87868C13.5586,6.31607 12.7956,6 11.9999,6C11.2043,6 10.4412,6.31607 9.87861,6.87868C9.31601,7.44129 8.99994,8.20435 8.99994,9C8.99994,9.79565 9.31601,10.5587 9.87861,11.1213C10.4412,11.6839 11.2043,12 11.9999,12C12.7956,12 13.5586,11.6839 14.1213,11.1213C14.6839,10.5587 14.9999,9.79565 14.9999,9ZM10.1671,13.9359C9.05618,13.5422 8.08119,12.7969 7.41087,11.7891L6.49681,10.4156C6.03744,9.72656 5.10931,9.54375 4.42025,10.0031C3.73119,10.4625 3.54369,11.3906 4.00306,12.0844L4.91712,13.4531C5.76556,14.7234 6.92337,15.7219 8.24994,16.3781V28.5C8.24994,29.3297 8.92025,30 9.74994,30C10.5796,30 11.2499,29.3297 11.2499,28.5V24H12.7499V28.5C12.7499,29.3297 13.4202,30 14.2499,30C15.0796,30 15.7499,29.3297 15.7499,28.5V16.3875C17.114,15.7219 18.2999,14.6906 19.1577,13.3781L20.0109,12.0703C20.4609,11.3766 20.264,10.4484 19.5702,9.99375C18.8765,9.53906 17.9484,9.73594 17.4937,10.4344L16.6406,11.7375C15.6187,13.3031 13.8796,14.25 12.0093,14.25C11.4187,14.25 10.8421,14.1562 10.2937,13.9781C10.2515,13.9641 10.2093,13.9453 10.1671,13.9359Z";

const ProfileTabIcon: React.FC<{ isSelected: boolean }> = ({ isSelected }) => (
  <TabIconSlot $isSelected={isSelected}>
    <svg viewBox="0 0 24 24" fill="currentColor">
      {isSelected ? (
        <path d={PROFILE_TAB_ICON_SOLID_PATH} />
      ) : (
        <>
          <clipPath id="profile-tab-icon-clip">
            <circle cx={12} cy={12} r={10} />
          </clipPath>
          <circle
            cx={12}
            cy={12}
            r={11}
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
          />
          <path
            d={PROFILE_TAB_ICON_REGULAR_FIGURE_PATH}
            clipPath="url(#profile-tab-icon-clip)"
          />
        </>
      )}
    </svg>
  </TabIconSlot>
);

// Staff tools, for users who can access school metadata.
const SCHOOL_ADMIN_TABS = [
  {
    tab: TabEnum.SchoolList,
    eventName: "School List",
    labelKey: "navigation_school_list",
    solid: faSchoolSolid,
    regular: faSchoolRegular,
  },
  {
    tab: TabEnum.SchoolGroupList,
    eventName: "District List",
    labelKey: "navigation_school_group_list",
    solid: faBuildingColumnsSolid,
    regular: faBuildingColumnsRegular,
  },
  {
    tab: TabEnum.SchoolRenewals,
    eventName: "School Renewals",
    labelKey: "navigation_school_renewals",
    solid: faCalendarCircleExclamationSolid,
    regular: faCalendarCircleExclamationRegular,
  },
  {
    tab: TabEnum.SchoolInsights,
    eventName: "School Insights",
    labelKey: "navigation_school_insights",
    solid: faChartLineSolid,
    regular: faChartLineRegular,
  },
];

/*

Responsive nav bar.

Elements in different states at different width breakpoints.
https://getbootstrap.com/docs/5.0/layout/breakpoints/

xs, sm:
- Header row: hamburger, logo
- Content row: main content column

md+:
- No header row
- Content row: sidebar (logo, tabs, settings, footer), main content column

Account actions live on the Profile tab (profile switcher) and the Settings
page (everything else).
*/

export const SuperTeacherNavbar: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const history = useHistory();
  const { accountLiveData } = React.useContext(AccountContext);
  const [userIsSignedUp, setUserIsSignedUp] = React.useState<boolean>();
  const [userIsSchoolUser, setUserIsSchoolUser] = React.useState<boolean>();
  const [userIsSchoolUserTeacher, setUserIsSchoolUserTeacher] =
    React.useState<boolean>();
  const [userCanAccessSchoolDashboards, setUserCanAccessSchoolDashboards] =
    React.useState<boolean>();
  const [userCanAccessTeacherPD, setUserCanAccessTeacherPD] =
    React.useState<boolean>();
  const [userCanAccessTeacherResources, setUserCanAccessTeacherResources] =
    React.useState<boolean>();
  const [userCanAccessSchoolMetadata, setUserCanAccessSchoolMetadata] =
    React.useState<boolean>();
  const [profileTabLabel, setProfileTabLabel] = React.useState("");
  const [isMainDropdownOpen, setIsMainDropdownOpen] = React.useState(false);
  const [profileSwitcherAccount, setProfileSwitcherAccount] =
    React.useState<Account | null>(null);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = React.useState(false);
  const profileLinkRef = React.useRef<HTMLAnchorElement>(null);
  const [isMobileProfileExpanded, setIsMobileProfileExpanded] =
    React.useState(false);
  const mainMenuAnchorRef = React.useRef<HTMLDivElement>(null);
  const { currentTab } = React.useContext(TabsContext);
  const { resetNavigationBoundary, navbarSpecialActions } =
    React.useContext(NavContext);
  const { t } = useTranslation();
  const { logEvent } = React.useContext(AnalyticsContext);
  const { verifyAge } = React.useContext(VerifyAgeContext);
  const mobileProfileSwitcherAccount = navbarSpecialActions
    ? null
    : profileSwitcherAccount;

  useLiveData(
    React.useCallback(
      (account: Account) => {
        if (account.hasUserAndMaybeProfile()) {
          setUserIsSchoolUserTeacher(isSchoolUserTeacher(account.user));
          setUserCanAccessSchoolDashboards(
            account.user.can_access_school_dashboards
          );
          setUserCanAccessTeacherPD(
            account.user.is_staff ||
              account.user.id === 11 || // Andrea
              account.user.id === 519227 || // Sean
              account.user.id === 82 || // Mike
              account.user.id === 518674 || // Sheynelle
              account.user.can_view_teacher_pd === true
          );
          setUserCanAccessTeacherResources(
            isNonStudentSchoolUser(account.user) ||
              account.user.is_staff ||
              account.user.id === 518674 || // Sheynelle
              account.user.id === 519227 || // Sean
              account.user.id === 11 // Andrea
          );
          setUserCanAccessSchoolMetadata(
            account.user.can_access_school_metadata
          );
          const isSignedUp = isUserSignedUp(account.user);
          setUserIsSignedUp(isSignedUp);
          setUserIsSchoolUser(isSchoolUser(account.user));
          setProfileSwitcherAccount((previous) => {
            if (!canSwitchProfile(account)) {
              return null;
            }
            const isUnchanged =
              previous?.user === account.user &&
              previous.profile === account.profile;
            return isUnchanged ? previous : account;
          });
          const firstName = account.displayProfile()?.first_name.trim();
          if (!isSignedUp) {
            setProfileTabLabel(t("navigation_login"));
          } else if (firstName) {
            setProfileTabLabel(firstName);
          } else {
            setProfileTabLabel(t("navigation_profile"));
          }
        }
      },
      [t]
    ),
    accountLiveData
  );

  const openTab = React.useCallback(
    (tab: TabEnum, eventName: string) => {
      setIsMainDropdownOpen(false);
      logEvent(new TabTapEvent(eventName));
      history.replace(tab);
      resetNavigationBoundary();
    },
    [history, logEvent, resetNavigationBoundary]
  );

  // School users go straight to Settings; everyone else clears the grown-ups
  // hurdle first.
  const openSettings = React.useCallback(() => {
    setIsMainDropdownOpen(false);
    logEvent(new TabTapEvent("Settings"));
    const navigate = () => {
      history.push(TabEnum.Settings);
      resetNavigationBoundary();
    };
    if (userIsSchoolUser) {
      navigate();
    } else {
      verifyAge((success: boolean) => {
        if (success) {
          navigate();
        }
      });
    }
  }, [history, logEvent, resetNavigationBoundary, userIsSchoolUser, verifyAge]);

  const wordmark = (
    <AppLink to={TabEnum.Home}>
      {/* translateY matches the marketing nav logo's optical alignment
          (.st-nav__logo in the getsuperteacher.com styles.css) */}
      <Image
        src={`${process.env.PUBLIC_URL}/wordmark.svg`}
        fluid
        style={{ transform: "translateY(12%)" }}
      />
    </AppLink>
  );

  return userIsSignedUp === undefined ? (
    <></>
  ) : (
    <div className="container-fluid vh-100 d-flex flex-column p-0">
      {/* Header visibility:
          - only at "xs" and "sm" breakpoints, unless the sidebar is replaced
            by navbarSpecialActions */}
      <Header
        className={navbarSpecialActions ? undefined : "d-md-none"}
        style={{ height: "fit-content" }}
      >
        <Stack direction="horizontal">
          {/* Hamburger menu visibility:
              - only show at "xs" and "sm" breakpoints */}
          <div className="d-md-none" style={{ paddingRight: "10px" }}>
            <div ref={mainMenuAnchorRef}>
              <FontAwesomeIcon
                icon={faBars}
                size="xl"
                onClick={() => {
                  setIsMobileProfileExpanded(false);
                  setIsMainDropdownOpen(!isMainDropdownOpen);
                }}
                role="button"
              />
              <Menu
                open={isMainDropdownOpen}
                onClose={() => setIsMainDropdownOpen(false)}
                anchorEl={mainMenuAnchorRef.current}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                disableAutoFocusItem
                slotProps={{
                  paper: {
                    style: {
                      width: 250,
                      marginTop: 6,
                    },
                  },
                }}
              >
                <MenuItem
                  onClick={() => openTab(TabEnum.Home, "Home")}
                  selected={currentTab === TabEnum.Home}
                >
                  <ListItemText>{t("navigation_home")}</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => openTab(TabEnum.Practice, "Practice")}
                  selected={currentTab === TabEnum.Practice}
                >
                  <ListItemText>{t("navigation_practice")}</ListItemText>
                </MenuItem>
                {userIsSchoolUserTeacher && (
                  <MenuItem
                    onClick={() => openTab(TabEnum.MyClass, "My Class")}
                    selected={currentTab === TabEnum.MyClass}
                  >
                    <ListItemText>{t("navigation_my_class")}</ListItemText>
                  </MenuItem>
                )}
                <MenuItem
                  onClick={() => openTab(TabEnum.Profile, "Profile")}
                  selected={currentTab === TabEnum.Profile}
                >
                  <ListItemText
                    primaryTypographyProps={{ noWrap: true }}
                    title={profileTabLabel}
                  >
                    {profileTabLabel}
                  </ListItemText>
                  {mobileProfileSwitcherAccount && (
                    <ProfileMenuToggle
                      isExpanded={isMobileProfileExpanded}
                      isTouch
                      onToggle={() =>
                        setIsMobileProfileExpanded(!isMobileProfileExpanded)
                      }
                    />
                  )}
                </MenuItem>
                {isMobileProfileExpanded && mobileProfileSwitcherAccount && (
                  <ProfileSwitcherItems
                    account={mobileProfileSwitcherAccount}
                    variant="inline"
                    onDone={() => setIsMainDropdownOpen(false)}
                  />
                )}
                {userCanAccessTeacherPD && (
                  <MenuItem
                    onClick={() => openTab(TabEnum.TeacherPD, "Teacher PD")}
                    selected={currentTab === TabEnum.TeacherPD}
                  >
                    <ListItemText>{t("navigation_teacher_pd")}</ListItemText>
                  </MenuItem>
                )}
                {userCanAccessSchoolDashboards && (
                  <MenuItem
                    onClick={() => openTab(TabEnum.Dashboard, "Dashboard")}
                    selected={currentTab === TabEnum.Dashboard}
                  >
                    <ListItemText>{t("navigation_dashboard")}</ListItemText>
                  </MenuItem>
                )}
                {userCanAccessTeacherResources && (
                  <MenuItem
                    onClick={() =>
                      openTab(TabEnum.TeacherResources, "Resources")
                    }
                    selected={currentTab === TabEnum.TeacherResources}
                  >
                    <ListItemText>
                      {t("navigation_teacher_resources")}
                    </ListItemText>
                  </MenuItem>
                )}
                {userCanAccessSchoolMetadata && [
                  <ListSubheader key="schools">
                    {t("navigation_schools_section")}
                  </ListSubheader>,
                  ...SCHOOL_ADMIN_TABS.map(({ tab, eventName, labelKey }) => (
                    <MenuItem
                      key={tab}
                      onClick={() => openTab(tab, eventName)}
                      selected={currentTab === tab}
                    >
                      <ListItemText>{t(labelKey)}</ListItemText>
                    </MenuItem>
                  )),
                  <Divider key="schools-end" />,
                ]}
                <MenuItem
                  onClick={openSettings}
                  selected={currentTab === TabEnum.Settings}
                >
                  <ListItemText>{t("navigation_settings")}</ListItemText>
                </MenuItem>
              </Menu>
            </div>
          </div>

          {/* "Super Teacher" logo */}
          <div className="me-auto" style={{ flex: "0 0 150px" }}>
            {wordmark}
          </div>
        </Stack>
      </Header>

      {/* One- or two-column layout:
          - flex-grow-1 ensures it takes all of vertical space, so that footer doesn't move up
          - "min-height: 0" is required for scrolling to work */}
      <div
        className="flex-grow-1 d-flex flex-row"
        style={{ minHeight: "0", backgroundColor: COLOR_WHITE }}
      >
        {/* Sidebar visibility:
            - If navbarSpecialActions is set, then sidebar is replaced with hamburger menu at all breakpoints
            - otherwise sidebar becomes visible at md breakpoint */}
        {!navbarSpecialActions && (
          <Sidebar className="d-none d-md-flex">
            <div style={{ width: 150, margin: "14px auto 24px" }}>
              {wordmark}
            </div>
            <Nav
              variant="pills"
              activeKey={currentTab}
              className="flex-column flex-grow-1"
            >
              <Nav.Link
                eventKey={TabEnum.Home}
                onClick={() => openTab(TabEnum.Home, "Home")}
              >
                <TabIcon
                  solid={faHouseSolid}
                  regular={faHouseRegular}
                  isSelected={currentTab === TabEnum.Home}
                />
                {t("navigation_home")}
              </Nav.Link>
              <Nav.Link
                eventKey={TabEnum.Practice}
                onClick={() => openTab(TabEnum.Practice, "Practice")}
              >
                <PracticeTabIcon isSelected={currentTab === TabEnum.Practice} />
                {t("navigation_practice")}
              </Nav.Link>
              {userIsSchoolUserTeacher && (
                <Nav.Link
                  eventKey={TabEnum.MyClass}
                  onClick={() => openTab(TabEnum.MyClass, "My Class")}
                >
                  <TabIcon
                    solid={faSchoolFlagSolid}
                    regular={faSchoolFlagRegular}
                    isSelected={currentTab === TabEnum.MyClass}
                  />
                  {t("navigation_my_class")}
                </Nav.Link>
              )}
              <Nav.Link
                ref={profileLinkRef}
                eventKey={TabEnum.Profile}
                onClick={() => openTab(TabEnum.Profile, "Profile")}
              >
                <ProfileTabIcon isSelected={currentTab === TabEnum.Profile} />
                <SingleLineText
                  title={profileTabLabel}
                  style={{ marginRight: 4 }}
                >
                  {profileTabLabel}
                </SingleLineText>
                {profileSwitcherAccount && (
                  <ProfileMenuToggle
                    isExpanded={isProfileMenuOpen}
                    isTouch={false}
                    onToggle={() => setIsProfileMenuOpen(true)}
                  />
                )}
              </Nav.Link>
              {profileSwitcherAccount && (
                <ProfileSwitcherMenu
                  account={profileSwitcherAccount}
                  anchorEl={profileLinkRef.current}
                  open={isProfileMenuOpen}
                  onClose={() => setIsProfileMenuOpen(false)}
                />
              )}
              {userCanAccessTeacherPD && (
                <Nav.Link
                  eventKey={TabEnum.TeacherPD}
                  onClick={() => openTab(TabEnum.TeacherPD, "Teacher PD")}
                >
                  <TabIcon
                    solid={faUserGraduateSolid}
                    regular={faUserGraduateRegular}
                    isSelected={currentTab === TabEnum.TeacherPD}
                  />
                  {t("navigation_teacher_pd")}
                </Nav.Link>
              )}
              {userCanAccessSchoolDashboards && (
                <Nav.Link
                  eventKey={TabEnum.Dashboard}
                  onClick={() => openTab(TabEnum.Dashboard, "Dashboard")}
                >
                  <TabIcon
                    solid={faChartColumnSolid}
                    regular={faChartColumnRegular}
                    isSelected={currentTab === TabEnum.Dashboard}
                  />
                  {t("navigation_dashboard")}
                </Nav.Link>
              )}
              {userCanAccessTeacherResources && (
                <Nav.Link
                  eventKey={TabEnum.TeacherResources}
                  onClick={() =>
                    openTab(TabEnum.TeacherResources, "Resources")
                  }
                >
                  <TabIcon
                    solid={faFolderBookmarkSolid}
                    regular={faFolderBookmarkRegular}
                    isSelected={currentTab === TabEnum.TeacherResources}
                  />
                  {t("navigation_teacher_resources")}
                </Nav.Link>
              )}
              {userCanAccessSchoolMetadata && (
                <>
                  <SidebarSectionHeader>
                    {t("navigation_schools_section")}
                  </SidebarSectionHeader>
                  {SCHOOL_ADMIN_TABS.map(
                    ({ tab, eventName, labelKey, solid, regular }) => (
                      <Nav.Link
                        key={tab}
                        eventKey={tab}
                        onClick={() => openTab(tab, eventName)}
                      >
                        <TabIcon
                          solid={solid}
                          regular={regular}
                          isSelected={currentTab === tab}
                        />
                        {t(labelKey)}
                      </Nav.Link>
                    )
                  )}
                </>
              )}
              {/* Settings is pinned to the bottom, above the footer */}
              <Nav.Link
                eventKey={TabEnum.Settings}
                onClick={openSettings}
                style={{ marginTop: "auto", justifyContent: "center" }}
              >
                <TabIcon
                  solid={faGearSolid}
                  regular={faGearRegular}
                  isSelected={currentTab === TabEnum.Settings}
                />
                {t("navigation_settings")}
              </Nav.Link>
            </Nav>
            <div
              style={{
                padding: "8px 6px",
                fontSize: "11px",
                color: COLOR_TEXT_SECONDARY,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div>© 2026 Super Teacher Inc.</div>
              <div style={{ display: "flex", gap: "8px", marginTop: "4px" }}>
                <AppLink themed to="/terms-of-use/">
                  {t("footer_terms")}
                </AppLink>
                <AppLink themed to="/privacy-policy/">
                  {t("footer_privacy")}
                </AppLink>
                {/* CC BY 4.0 attributions for the standards data. */}
                <AppLink themed to="/standards-attributions/">
                  {t("footer_attributions")}
                </AppLink>
              </div>
            </div>
          </Sidebar>
        )}
        <div className="d-flex flex-column flex-grow-1 overflow-auto">
          {/* The main content should fill 100% of the vertical space with flex-grow-1
                so that the footer is no higher than the bottom of the screen */}
          <ContentArea className="flex-grow-1">{children}</ContentArea>
          <div className="flex-shrink-0">
            <SuperTeacherFooter />
          </div>
        </div>
      </div>
    </div>
  );
};
